import {
  Box,
} from '@cloudscape-design/components';
import { SpaceBetween } from '@cloudscape-design/components';
import { useMemo, useState } from 'react';
import { ReviewBasic } from '../common/types';
import { ReviewContent } from './ReviewContent';
import { formatDateString } from '../common/formatting/dateFormat';
import { formatSourceName } from '../common/formatting/sourceFormat';
import { ReviewText } from './ReviewText';
import { ChatBubble, Avatar } from '@cloudscape-design/chat-components';
import { PropertyFilterQuery, useCollection } from '@cloudscape-design/collection-hooks';
import { ViolationBadge } from './ViolationBadge';

export type SimpleReviewListProps = {
  reviews: ReviewBasic[];
  placeName: string;
  header?: React.ReactNode;
  cardsPerRow?: number;
  includeResponse?: boolean;
  truncateCharacters?: number;
  maxCards?: number;
};

export function SimpleReviewList(props: SimpleReviewListProps) {
  const { reviews, placeName, cardsPerRow, includeResponse, truncateCharacters, maxCards } = props;

  const initials = useMemo(() => {
    // if multiple words, use first letter of first two words
    if (placeName.includes(' ')) {
      return placeName
        .split(' ')
        .slice(0, 2)
        .map((word) => word[0])
        .join('');
    }
    // if one word but more than 2 capital letters, use first two capital letters
    let initials = '';
    for (const char of placeName) {
      if (char.toUpperCase() === char) {
        initials += char;
        if (initials.length === 2) {
          break;
        }
      }
    }
    if (initials.length === 0) {
      return placeName.slice(0, 2);
    }
  }, [placeName]);

  const [filteringText, setFilteringText] = useState('');
  const [textFilter, setTextFilter] = useState('');

  const [query, setQuery] = useState<PropertyFilterQuery>({
    tokens: [],
    operation: 'and',
  });

  const filteredReviews = useMemo(() => {
    const filtered = reviews.filter((review) => {
      let match = true;
      const textMatch =
        review.content?.toLowerCase().includes(textFilter.toLowerCase()) ||
        review.response_content?.toLowerCase().includes(textFilter.toLowerCase());

      match = match && !!textMatch;

      if (query.tokens.length > 0) {
        for (const token of query.tokens) {
          if (token.propertyKey === 'stars') {
            if (token.operator === '=') {
              match = match && review.stars === parseInt(token.value);
            } else if (token.operator === '!=') {
              match = match && review.stars !== parseInt(token.value);
            } else if (token.operator === '>=') {
              match = match && (review.stars ?? 0) >= parseInt(token.value);
            } else if (token.operator === '<=') {
              match = match && (review.stars ?? 0) <= parseInt(token.value);
            } else if (token.operator === '>') {
              match = match && (review.stars ?? 0) > parseInt(token.value);
            } else if (token.operator === '<') {
              match = match && (review.stars ?? 0) < parseInt(token.value);
            }
          } else if (token.propertyKey === 'response') {
            if (token.operator === '=') {
              if (token.value === 'true') {
                match = match && !!review.response_content;
              } else if (token.value === 'false') {
                match = match && !review.response_content;
              }
            }
          }
        }
      }

      return match;
    });

    if (maxCards) {
      return filtered.slice(0, maxCards);
    }

    return filtered;
  }, [reviews, textFilter, query, maxCards]);

  return (
    <SpaceBetween direction="vertical" size="m">
      {
        filteredReviews.map((review) => (
          <SpaceBetween direction="vertical" size="xxxs" key={review.uuid}>
            <SimpleReviewCardContent
              review={review}
              truncateCharacters={truncateCharacters}
              textFilter={textFilter}
            />
            <SimpleReviewResponseContent
              review={review}
              truncateCharacters={truncateCharacters}
              textFilter={textFilter}
              includeResponse={includeResponse}
              placeName={placeName}
              initials={initials}
            />
          </SpaceBetween>
        ))}
    </SpaceBetween>
  );
}


export type SimpleReviewCardContentProps = {
  review: ReviewBasic;
  truncateCharacters?: number;
  textFilter?: string;
};

export function SimpleReviewCardContent(props: SimpleReviewCardContentProps) {
  const { review, truncateCharacters, textFilter } = props;

  const initials = useMemo(() => {
    if (review.author) {
      return review.author
        .split(' ')
        .filter((word) => word.length > 0)
        .map((word) => word[0].toUpperCase())
        .join('');
    }
    return undefined;
  }, [review.author]);

  const violations = review.violations ?? [];

  return (
  <ChatBubble
    key={review.uuid + '-review'}
    ariaLabel="Review"
    type="incoming"
    avatar={
      <Avatar
        key={review.uuid + '-review-avatar'}
        initials={initials}
        ariaLabel={
          review.review_date
            ? formatDateString(review.review_date)
            : (review.recency ?? '')
        }
        tooltipText={`From ${review.author ?? formatSourceName(review.source)} on ${
          review.review_date
            ? formatDateString(review.review_date)
            : (review.recency ?? '')
        }`}
      />
    }
  >
    <ReviewContent
      key={review.uuid + '-review-content'}
      review_id={review.review_id ?? undefined}
      text={review.content}
      stars={review.stars ?? undefined}
      truncateCharacters={truncateCharacters}
      recency={
        review.review_date ? formatDateString(review.review_date) : (review.recency ?? undefined)
      }
      highlights={textFilter ? [textFilter] : undefined}
      highlightCaseSensitive={false}
      displaySource
      source={review.source ?? undefined}
      link={review.link ?? undefined}
    />
    <ViolationBadge violations={violations} source={review.source} />
</ChatBubble>

  );
}


export type SimpleReviewResponseContentProps = {
  review: ReviewBasic;
  truncateCharacters?: number;
  textFilter?: string;
  includeResponse?: boolean;
  placeName: string;
  initials?: string;
};
export function SimpleReviewResponseContent(props: SimpleReviewResponseContentProps) {
  const { review, truncateCharacters, textFilter, includeResponse, placeName, initials } = props;

  return (
    review.response_content ? (
      includeResponse ? (
        <ChatBubble
          key={review.uuid + '-response'}
          ariaLabel="Review Response"
          type="outgoing"
          avatar={
            <Avatar
              key={review.uuid + '-response-avatar'}
              ariaLabel={placeName}
              tooltipText={placeName}
              initials={initials}
              color="gen-ai"
            />
          }
        >
          <ReviewContent
            key={review.uuid + '-response-content'}
            text={review.response_content}
            recency={
              review.response_date ? formatDateString(review.response_date) : undefined
            }
            highlights={textFilter ? [textFilter] : undefined}
            highlightCaseSensitive={false}
            truncateCharacters={truncateCharacters}
          />
        </ChatBubble>
      ) : review.response_date ? (
        <Box variant="p" textAlign="center" key={review.uuid + '-response'}>
          (Responded on {formatDateString(review.response_date)})
        </Box>
      ) : null
    ) : (
      <Box variant="p" textAlign="center" key={review.uuid + '-response-no-response'}>
        (No response)
      </Box>
    )
  );
}